import { cn } from '@/lib/cn'
import { typoNode } from '@/lib/typo'

/**
 * The one page header of the admin (design §3.2, WP-06): the page's only `<h1>` in `title-1`
 * Onest 600, an optional description in `body` ink-2 (at most 60 characters a line), actions on
 * the right (they wrap under the title on a phone) and an optional tabs row below.
 * Long explanations (formulas) do not go here: pass them as `details` — a folded «Как
 * считается» under the description. `foldOnPhone`: below `md` the description itself folds
 * under «Подробнее» — a page whose list must start on the first screen at 320 px (WP-15).
 */
export function PageHeader({
  title,
  description,
  actions,
  tabs,
  details,
  detailsLabel = 'Как считается',
  foldOnPhone = false,
  className,
}: {
  title: string
  description?: React.ReactNode
  actions?: React.ReactNode
  tabs?: React.ReactNode
  details?: React.ReactNode
  detailsLabel?: string
  foldOnPhone?: boolean
  className?: string
}) {
  return (
    <header className={cn('mb-6 flex flex-col gap-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-title-1 font-semibold text-ink-1 [overflow-wrap:anywhere]">{typoNode(title)}</h1>
          {description && (
            <p className={cn('mt-2 max-w-lead text-body text-ink-2 [overflow-wrap:anywhere]', foldOnPhone && 'hidden md:block')}>
              {typoNode(description)}
            </p>
          )}
          {description && foldOnPhone && (
            <details className="mt-2 max-w-lead text-body text-ink-2 md:hidden">
              <summary className="w-fit cursor-pointer rounded-xs text-caption text-ink-2 underline decoration-hairline-strong underline-offset-4 hover:text-ink-1 [@media(pointer:coarse)]:py-3">
                Подробнее
              </summary>
              <p className="mt-2 [overflow-wrap:anywhere]">{typoNode(description)}</p>
            </details>
          )}
          {details && (
            <details className="mt-2 max-w-lead text-body text-ink-2">
              <summary className="w-fit cursor-pointer rounded-xs text-caption text-ink-2 underline decoration-hairline-strong underline-offset-4 hover:text-ink-1">
                {detailsLabel}
              </summary>
              <div className="mt-2 text-caption text-ink-2">{typoNode(details)}</div>
            </details>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {tabs}
    </header>
  )
}
